/**
 * Phase 5.9.9 — Dispatcher / SA Header Menu (hamburger).
 *
 * Collapses 10 secondary header buttons into a structured 3-section dropdown:
 *   OPERATIONS    — Push Notifications · Settings · Broadcast
 *   ADMIN (SA)    — Users · Points · Regions · Leaderboards
 *   PERSONAL      — My QR · Security · Invite
 *
 * The Admin group is hidden for regional dispatchers, so their menu stays
 * short (~7 items). PushSubscribeButton is included inline because it carries
 * its own clickable state — we render it as a custom menu item.
 *
 * Renders nothing for non-dispatcher roles (defensive — parent already gates).
 */
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Menu, Settings, Megaphone, Users, Award, Globe, Trophy,
  QrCode, Lock, Share2, Bell, Mail, AlertTriangle, ShieldCheck, Sparkles, UserCog, Star,
  Activity,
  Clock,
  ShieldAlert,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
} from './ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { PushSubscribeButton } from './PushSubscribeButton';
import { InviteShareButton } from './InviteShareButton';

export function DispatcherHeaderMenu({ broadcastEnabled = false }) {
  const { user } = useAuth();
  // Feb 2026 — Pending review count for the moderation menu badge. Read
  // once on mount + every 60s while the menu component lives. Failure
  // is silent — badge just stays 0.
  const [pendingReviews, setPendingReviews] = useState(0);
  const [watchFlags, setWatchFlags] = useState(0);
  useEffect(() => {
    if (!user || !['dispatcher', 'super_admin'].includes(user.role)) return undefined;
    let alive = true;
    const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    const fetchCount = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/admin/reviews/pending-count`,
          { withCredentials: true },
        );
        if (alive) setPendingReviews(Number(data.count || 0));
      } catch { /* silent */ }
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/points-watch/flag-count`, { withCredentials: true });
        if (alive) setWatchFlags(Number(data.count || 0));
      } catch { /* silent */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  if (!user || !['dispatcher', 'super_admin'].includes(user.role)) return null;
  const isSuperAdmin = user.role === 'super_admin';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none font-mono h-9 px-3"
          data-testid="dispatcher-menu-trigger"
          aria-label="Open dispatcher menu"
        >
          <Menu className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">MENU</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="bg-[#0C0612] border border-[#2A0F3D] text-[#A3C7A8] font-mono min-w-[220px] rounded-none"
        data-testid="dispatcher-menu-content"
      >
        {/* ============ OPERATIONS ============ */}
        <DropdownMenuLabel className="text-[#39FF14] uppercase text-[10px] tracking-wider">
          // Operations
        </DropdownMenuLabel>
        <DropdownMenuGroup>
          {/* Push toggle — render the existing component inline as a menu row */}
          <div
            className="px-2 py-1.5 text-xs flex items-center gap-2"
            data-testid="dispatcher-menu-push-row"
          >
            <Bell className="w-3.5 h-3.5 text-[#A3C7A8]" />
            <PushSubscribeButton testId="dispatch-push-toggle" />
          </div>
          <DropdownMenuItem asChild>
            <Link
              to="/dispatch/settings"
              data-testid="dispatcher-menu-settings"
              className="cursor-pointer hover:bg-[rgba(57,255,20,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
            >
              <Settings className="w-3.5 h-3.5" />
              SETTINGS
            </Link>
          </DropdownMenuItem>
          {broadcastEnabled && (
            <DropdownMenuItem asChild>
              <Link
                to="/dispatch/broadcast"
                data-testid="dispatcher-menu-broadcast"
                className="cursor-pointer hover:bg-[rgba(57,255,20,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
              >
                <Megaphone className="w-3.5 h-3.5" />
                BROADCAST
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <Link
              to="/dispatch/admin/edit-profile"
              data-testid="dispatcher-menu-edit-profile"
              className="cursor-pointer hover:bg-[rgba(163,199,168,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
            >
              <UserCog className="w-3.5 h-3.5 text-[#A3C7A8]" />
              EDIT MY PROFILE
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to="/dispatch/admin/points-watch"
              data-testid="dispatcher-menu-points-watch"
              className="cursor-pointer hover:bg-[rgba(255,59,48,0.08)] rounded-none text-xs flex items-center justify-between gap-2 px-2 py-1.5"
            >
              <span className="flex items-center gap-2">
                <ShieldAlert className="w-3.5 h-3.5 text-[#FF3B30]" />
                POINTS WATCH
              </span>
              {watchFlags > 0 && (
                <span className="bg-[#FF3B30] text-white font-bold text-[10px] px-1.5 py-0.5 min-w-[18px] text-center" data-testid="dispatcher-menu-points-watch-badge">
                  {watchFlags > 99 ? '99+' : watchFlags}
                </span>
              )}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to="/dispatch/admin/delivery-slots"
              data-testid="dispatcher-menu-delivery-slots"
              className="cursor-pointer hover:bg-[rgba(0,240,255,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
            >
              <Clock className="w-3.5 h-3.5 text-[#00F0FF]" />
              DELIVERY SLOTS
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to="/dispatch/admin/failed-orders"
              data-testid="dispatcher-menu-failed-orders"
              className="cursor-pointer hover:bg-[rgba(255,59,48,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
            >
              <AlertTriangle className="w-3.5 h-3.5 text-[#FF3B30]" />
              CHECKOUT REPORT
            </Link>
          </DropdownMenuItem>
          {isSuperAdmin && (
            <DropdownMenuItem asChild>
              <Link
                to="/dispatch/admin/data-quality"
                data-testid="dispatcher-menu-data-quality"
                className="cursor-pointer hover:bg-[rgba(212,130,59,0.10)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-[#D4823B]" />
                DATA QUALITY
              </Link>
            </DropdownMenuItem>
          )}
          {isSuperAdmin && (
            <DropdownMenuItem asChild>
              <Link
                to="/dispatch/admin/promo-agent"
                data-testid="dispatcher-menu-promo-agent"
                className="cursor-pointer hover:bg-[rgba(255,61,255,0.10)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#FF3DFF]" />
                PROMO AGENT
              </Link>
            </DropdownMenuItem>
          )}
          {/* Feb 2026 — Track B moderation surface. Visible to both
              dispatcher + super_admin roles (matches route guard). Badge
              shows pending-queue depth so SA can spot backlog at a glance. */}
          <DropdownMenuItem asChild>
            <Link
              to="/dispatch/admin/reviews"
              data-testid="dispatcher-menu-reviews"
              className="cursor-pointer hover:bg-[rgba(255,214,10,0.10)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5 justify-between"
            >
              <span className="flex items-center gap-2">
                <Star className="w-3.5 h-3.5 text-[#FFD60A]" />
                REVIEWS
              </span>
              {pendingReviews > 0 && (
                <span
                  className="bg-[#FFD60A] text-[#050003] font-bold text-[10px] px-1.5 py-0.5 rounded-none min-w-[18px] text-center"
                  data-testid="dispatcher-menu-reviews-badge"
                >
                  {pendingReviews > 99 ? '99+' : pendingReviews}
                </span>
              )}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to="/dispatch/admin/launch-stats"
              data-testid="dispatcher-menu-launch-stats"
              className="cursor-pointer hover:bg-[rgba(0,240,255,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
            >
              <Activity className="w-3.5 h-3.5 text-[#00F0FF]" />
              LAUNCH STATS
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to="/dispatch/admin/push-health"
              data-testid="dispatcher-menu-push-health"
              className="cursor-pointer hover:bg-[rgba(255,159,10,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
            >
              <Bell className="w-3.5 h-3.5 text-[#FF9F0A]" />
              PUSH HEALTH
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to="/dispatch/admin/email-health"
              data-testid="dispatcher-menu-email-health"
              className="cursor-pointer hover:bg-[rgba(255,59,48,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
            >
              <Mail className="w-3.5 h-3.5 text-[#FF3B30]" />
              EMAIL HEALTH
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to="/dispatch/admin/login-stats"
              data-testid="dispatcher-menu-login-stats"
              className="cursor-pointer hover:bg-[rgba(57,255,20,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
            >
              <Users className="w-3.5 h-3.5 text-[#39FF14]" />
              LOGIN STATS
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        {/* ============ ADMIN (SA only) ============ */}
        {isSuperAdmin && (
          <>
            <DropdownMenuSeparator className="bg-[#2A0F3D]" />
            <DropdownMenuLabel className="text-[#FF9F0A] uppercase text-[10px] tracking-wider">
              // Admin
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link
                  to="/dispatch/admin/users"
                  data-testid="dispatcher-menu-users"
                  className="cursor-pointer hover:bg-[rgba(255,159,10,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
                >
                  <Users className="w-3.5 h-3.5 text-[#FF9F0A]" />
                  USERS
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/dispatch/admin/points"
                  data-testid="dispatcher-menu-points"
                  className="cursor-pointer hover:bg-[rgba(255,159,10,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
                >
                  <Award className="w-3.5 h-3.5 text-[#FF9F0A]" />
                  POINTS
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/dispatch/admin/regions"
                  data-testid="dispatcher-menu-regions"
                  className="cursor-pointer hover:bg-[rgba(0,240,255,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
                >
                  <Globe className="w-3.5 h-3.5 text-[#00F0FF]" />
                  REGIONS
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/dispatch/admin/leaderboards"
                  data-testid="dispatcher-menu-leaderboards"
                  className="cursor-pointer hover:bg-[rgba(255,159,10,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
                >
                  <Trophy className="w-3.5 h-3.5 text-[#FF9F0A]" />
                  LEADERBOARDS
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/dispatch/admin/boost"
                  data-testid="dispatcher-menu-boost"
                  className="cursor-pointer hover:bg-[rgba(57,255,20,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
                >
                  <span className="text-[#39FF14]">⚡</span>
                  BOOST
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        )}

        {/* ============ PERSONAL ============ */}
        <DropdownMenuSeparator className="bg-[#2A0F3D]" />
        <DropdownMenuLabel className="text-[#A3C7A8] uppercase text-[10px] tracking-wider">
          // Personal
        </DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link
              to="/staff/rewards"
              data-testid="dispatcher-menu-my-qr"
              className="cursor-pointer hover:bg-[rgba(57,255,20,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
            >
              <QrCode className="w-3.5 h-3.5" />
              MY QR
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to="/account/security"
              data-testid="dispatcher-menu-security"
              className="cursor-pointer hover:bg-[rgba(57,255,20,0.08)] rounded-none text-xs flex items-center gap-2 px-2 py-1.5"
            >
              <Lock className="w-3.5 h-3.5" />
              SECURITY
            </Link>
          </DropdownMenuItem>
          <div
            className="px-2 py-1.5 text-xs flex items-center gap-2"
            data-testid="dispatcher-menu-invite-row"
          >
            <Share2 className="w-3.5 h-3.5 text-[#A3C7A8]" />
            <InviteShareButton testId="dispatcher-invite-share" accent="#A3C7A8" />
          </div>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
